import React, { FormEvent, Dispatch } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

// types
import { FieldNames } from 'js/auth/types/field';
import { Search } from 'js/auth/types/search';
import { RegexValidation } from 'js/common/types/regexValidation';
import { ReducerAction } from '../types/registration';

// styles
import { VercelLineDivider } from 'js/common/components/Vercel/styles/common';
import { VercelPasswordInput, VercelFormInput, VercelRedirectText, VercelGoogleAuthButton } from './styles/vercel';

// components
import VercelPage from 'js/common/components/Vercel/VercelPage';
import VercelLayout from 'js/common/components/Vercel/VercelLayout';
import TermsOfServiceCheckbox from './TermsOfServiceCheckbox';

interface Props {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  tos: boolean;
  search: Search;
  fieldValidationMap: { [key in FieldNames]?: RegexValidation };
  errorField: FieldNames | '';
  errorMessage: string;
  isSubmitDisabled: boolean;
  dispatch: Dispatch<ReducerAction>;
  onSubmit(e: FormEvent<HTMLFormElement>): void;
  isGoogleAuthEnabled: boolean;
  isSocialSignupDisabled: boolean;
  clientState?: Search;
}

const VercelTermsOfServiceCheckbox = styled(TermsOfServiceCheckbox)`
  margin: 24px 0 0;
  justify-content: left;
  span {
    flex-grow: 0;
    font-size: 13px;
    font-weight: 600;
  }
`;

const VercelPasswordTooltipParagraph = styled.p`
  margin: 0;
  padding: 0;
`;

export default function VercelRegistrationForm({
  username,
  password,
  firstName,
  lastName,
  tos,
  search,
  fieldValidationMap,
  errorField,
  errorMessage,
  isSubmitDisabled,
  isGoogleAuthEnabled,
  isSocialSignupDisabled,
  clientState,
  dispatch,
  onSubmit,
}: Props) {
  const isPasswordError = errorField === FieldNames.PASSWORD;
  const isUsernameError = errorField === FieldNames.USERNAME;
  const isFirstNameError = errorField === FieldNames.FIRST_NAME;
  const isLastNameError = errorField === FieldNames.LAST_NAME;

  return (
    <VercelPage pageTitle="Create Account">
      <VercelLayout
        title="Create Your MongoDB Account"
        isForm
        formProps={{ onSubmit, buttonText: 'Sign up', disabled: isSubmitDisabled }}
      >
        {isGoogleAuthEnabled && (
          <>
            <VercelGoogleAuthButton
              clientState={clientState}
              disabled={isSocialSignupDisabled}
              displayText="Sign up with Google"
              location={location}
            />
            <VercelLineDivider>or</VercelLineDivider>
          </>
        )}
        <VercelFormInput
          fieldName="emailAddress"
          labelName="Email Address"
          autoComplete="username"
          type="email"
          onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.USERNAME, value: e.target.value } })}
          value={username}
          regexValidation={fieldValidationMap[FieldNames.USERNAME]}
          hasError={isUsernameError}
          errorMessage={errorMessage}
          tooltipText={
            'This is your email address associated with Vercel. This integration will sign you up for' +
            ' Atlas using the same email address, but you will be able to change it later via the Atlas UI.'
          }
          disabled
        />
        <VercelFormInput
          fieldName={FieldNames.FIRST_NAME}
          labelName="First Name"
          autoComplete="given-name"
          onChange={(e) =>
            dispatch({ type: 'field', payload: { field: FieldNames.FIRST_NAME, value: e.target.value } })
          }
          value={firstName}
          regexValidation={fieldValidationMap[FieldNames.FIRST_NAME]}
          hasError={isFirstNameError}
          errorMessage={errorMessage}
        />
        <VercelFormInput
          fieldName={FieldNames.LAST_NAME}
          labelName="Last Name"
          autoComplete="family-name"
          onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.LAST_NAME, value: e.target.value } })}
          value={lastName}
          regexValidation={fieldValidationMap[FieldNames.LAST_NAME]}
          hasError={isLastNameError}
          errorMessage={errorMessage}
        />
        <VercelPasswordInput
          onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.PASSWORD, value: e.target.value } })}
          value={password}
          hasError={isPasswordError}
          errorMessage={errorMessage}
          forNewUser
          showRequirementsBox={false}
          tooltipText={
            <VercelPasswordTooltipParagraph>
              Your password must:
              <br />
              Contain at least 8 characters
              <br />
              Contain unique characters, numbers, or symbols
              <br />
              Not contain your email address
            </VercelPasswordTooltipParagraph>
          }
        />
        <VercelRedirectText>
          Already have an Atlas account?
          <Link to={`/login/vercel${!!search ? search + '&' : '?'}signedOut=true`}>&nbsp;Log in now</Link>
        </VercelRedirectText>
        <VercelTermsOfServiceCheckbox
          checked={tos}
          onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.TOS, value: e.target.checked } })}
        />
      </VercelLayout>
    </VercelPage>
  );
}
